import type {
  InvoiceV2AssigneeSuggestionsQueryParams,
  InvoiceV2AssigneeSuggestionsResponse,
} from './types';

import {
  queryOptions,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { queryKeysInvoice } from '../queryKeys';

import { fetchInvoiceV2AssigneeSuggestions } from './fetchInvoiceV2AssigneeSuggestions';

/**
 * Generates the query options for the invoice list assignee suggestions so
 * they can be shared between the useQuery hook and imperative fetching.
 *
 * @param queryParams - Optional search term and page window.
 * @param options - Additional options for the query.
 */
export const getInvoiceV2AssigneeSuggestionsQueryOptions = <
  TData = InvoiceV2AssigneeSuggestionsResponse,
>(
  queryParams: Partial<InvoiceV2AssigneeSuggestionsQueryParams> = {},
  options?: Omit<
    UseQueryOptions<InvoiceV2AssigneeSuggestionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  queryOptions({
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchInvoiceV2AssigneeSuggestions(queryParams, { signal }),
    queryKey: queryKeysInvoice.getAssigneeSuggestions(queryParams),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query hook for the users assigned to at least one invoice the caller
 * can read.
 *
 * @param queryParams - Optional search term and page window.
 * @param options - Additional options for the useQuery hook.
 */
// fallow-ignore-next-line unused-export -- canonical hook for the suggestion endpoint
export const useQueryInvoiceV2AssigneeSuggestions = <
  TData = InvoiceV2AssigneeSuggestionsResponse,
>(
  queryParams?: Partial<InvoiceV2AssigneeSuggestionsQueryParams>,
  options?: Omit<
    UseQueryOptions<InvoiceV2AssigneeSuggestionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getInvoiceV2AssigneeSuggestionsQueryOptions<TData>(queryParams, options),
  );
